// Copy for the main marketing page (thenotfit.com) in English, Albanian and Italian.
// FAQ answers may contain {offer} and {location}: the page swaps in prices in the visitor's currency.

export type Lang = "en" | "sq" | "it"
export type ModuleKey = "business" | "classes" | "workout" | "diet" | "store" | "whitelabel" | "marketing"

export const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "sq", label: "Shqip" },
  { code: "it", label: "Italiano" },
]

export interface LandingCopy {
  meta: { title: string; description: string; ogLocale: string }
  announcement: { eyebrow: string; message: string; cta: string }
  nav: { signIn: string; getStarted: string; features: string; showcase: string; pricing: string; customers: string; faq: string; menu: string }
  hero: {
    badge: string
    heading: string
    rotating: string[]
    rotatingSr: string
    description: string
    cta1: string
    cta2: string
    footnote: string[]
    offerPill: string
    tenantsLabel: string
  }
  marquee: { heading: string; items: string[] }
  stats: { heading: string; gyms: string; activeMembers: string; uptimeSla: string; support: string }
  features: {
    eyebrow: string; heading: string; subheading: string; valueProp: string
    cards: { title: string; desc_short: string; desc: string }[]
  }
  showcase: {
    eyebrow: string; heading: string; subheading: string
    tabs: { diet: string; workout: string; promo: string }
    comingSoon: string
    dietDesc: string; workoutDesc: string; promoDesc: string
  }
  steps: { eyebrow: string; heading: string; subheading: string; items: { title: string; desc: string }[] }
  mobile: { eyebrow: string; heading: string; sub: string; bullet1: string; bullet2: string; bullet3: string }
  benefits: {
    eyebrow: string
    heading: string // contains "NOT" as a sentinel — the BrandMark renders in its place
    subheading: string
    items: { title: string; desc: string }[]
  }
  pricing: {
    eyebrow: string; heading: string; subheading: string
    comingSoon: string; soon: string; perMonth: string; perMonthEach: string
    modules: Record<ModuleKey, { name: string; features: string[] }>
    extraLocations: { name: string; features: string[] }
    calculator: {
      title: string; selectAll: string; extraLocations: string
      modules: (n: number) => string; extraLocs: (n: number) => string; total: string
    }
    cta: { sub: string; btn1: string; btn2: string }
    offer: {
      eyebrow: string; heading: string; subheading: string
      unit: string; compare: string; cta: string
    }
  }
  caseStudy: {
    eyebrow: string; heading1: string; heading2: string; description: string
    gyms: { subtitle: string; members: string; highlight: string; features: string[] }[]
    followInstagram: string; testimonial: string; operator: string; statsLabels: string[]
  }
  faq: { eyebrow: string; heading: string; subheading: string; items: { q: string; a: string }[]; contact: string; contactCta: string }
  cta: {
    heading1: string; heading2: string; description: string
    btn1: string; btn2: string; check1: string; check2: string; check3: string
  }
  demoSubject: string
  footer: {
    tagline: string
    productHeading: string; productLinks: { label: string; href: string }[]
    legalHeading: string; legalLinks: { label: string; href: string }[]
    copyright: string
  }
}

export const translations: Record<Lang, LandingCopy> = {
  // ── English ────────────────────────────────────────────────────────────────
  en: {
    meta: {
      title: "THE NOT FIT — All-in-one gym management software with AI",
      description: "Memberships, QR check-ins, class bookings, payments, AI diet & workout plans and your own branded iOS & Android app. Start your 14-day free trial.",
      ogLocale: "en_US",
    },
    announcement: { eyebrow: "Limited time", message: "€499/year per location · Up to 90% off monthly pricing", cta: "See offer" },
    nav: { signIn: "Sign in", getStarted: "Get started", features: "Features", showcase: "Showcase", pricing: "Pricing", customers: "Customers", faq: "FAQ", menu: "Open menu" },
    hero: {
      badge: "AI-powered gym management",
      heading: "The all-in-one platform for",
      rotating: ["gyms", "fitness studios", "CrossFit boxes", "gym chains"],
      rotatingSr: "gyms, fitness studios, CrossFit boxes and gym chains",
      description: "Memberships, QR check-ins, class bookings, payments, AI diet & workout plans and your own branded mobile app — all in one platform built for modern gyms.",
      cta1: "Start free trial",
      cta2: "Book a demo",
      footnote: ["No credit card", "14-day trial", "Cancel anytime"],
      offerPill: "Limited-time launch offer · €499/year per location",
      tenantsLabel: "One platform, every gym's own brand:",
    },
    marquee: {
      heading: "Everything your gym runs on",
      items: ["QR check-ins", "Class bookings", "Recurring payments", "AI diet plans", "AI workout plans", "In-gym store", "Branded iOS & Android app", "Live analytics", "Multi-location", "Push notifications"],
    },
    stats: { heading: "Powering gyms every single day", gyms: "Gyms", activeMembers: "Active members", uptimeSla: "Uptime SLA", support: "Support" },
    features: {
      eyebrow: "Platform",
      heading: "Everything to run a modern gym",
      subheading: "Modular tools that automate, optimise, and personalise every part of your fitness business.",
      valueProp: "AI does the work. You run the gym.",
      cards: [
        { title: "White-label multi-tenancy", desc_short: "Built for chains and franchises", desc: "Manage unlimited locations from one platform. Performance trends per site; each gym keeps its own brand and independence." },
        { title: "AI promo builder",          desc_short: "Boost revenue with smart promos", desc: "AI designs targeted promotions, finds the best timing, and personalises discounts to maximise conversions." },
        { title: "AI class scheduling",       desc_short: "Optimised booking management",   desc: "AI analyses attendance and demand to suggest optimal schedules, auto-fill waitlists, and balance trainer workloads." },
        { title: "Payment processing",        desc_short: "Automated revenue management",   desc: "Recurring billing, payment plans, invoicing, and AI-generated forecasts. Accept all major payment methods." },
        { title: "Predictive analytics",      desc_short: "Business intelligence, live",    desc: "AI surfaces revenue trends, predicts peak hours, identifies at-risk members, and recommends actions in real time." },
        { title: "AI nutrition & training",   desc_short: "Personal plans for every member", desc: "AI builds personalised diet and workout plans from goals, body metrics, and progress — and adapts them automatically." },
      ],
    },
    showcase: {
      eyebrow: "Showcase",
      heading: "See AI in action",
      subheading: "Each gym gets a fully branded experience. Here is how the same AI tools look across different fitness businesses on our platform.",
      tabs: { diet: "AI Diet Planner", workout: "AI Workout Builder", promo: "AI Promo Builder" },
      comingSoon: "COMING SOON",
      dietDesc: "AI generates personalised meal plans with macro tracking, food databases, and automatic adjustments — branded to each gym.",
      workoutDesc: "Smart programs built around fitness level, recovery data, and performance trends. Plans evolve as members progress.",
      promoDesc: "AI-powered promotions that target the right members at the right time to maximise revenue and retention.",
    },
    steps: {
      eyebrow: "How it works",
      heading: "Live in three steps",
      subheading: "From sign-up to your first member check-in, without the usual software headaches.",
      items: [
        { title: "Create your account", desc: "Pick the modules you need and set up your gym and locations in minutes." },
        { title: "Make it yours",       desc: "Add your logo and brand colours. Your members get a branded web and mobile experience." },
        { title: "Go live",             desc: "Add members, publish your class schedule and start taking payments and check-ins." },
      ],
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "Native iOS & Android apps for every gym",
      sub: "Members get a fully branded mobile experience. You stay in control of every release.",
      bullet1: "Custom branding — logo, colors, app icon",
      bullet2: "QR check-in, bookings, plans, and store",
      bullet3: "Push notifications and in-app messages",
    },
    benefits: {
      eyebrow: "Why us",
      heading: "Why THE NOT FIT?",
      subheading: "The most advanced AI-powered fitness management platform, trusted by industry leaders.",
      items: [
        { title: "AI-Powered Diet Management", desc: "Generate personalised nutrition plans in seconds. AI adapts macros and meal schedules based on each member's goals, allergies, and body metrics." },
        { title: "AI Workout Plan Builder",    desc: "Smart training programs built around fitness level, recovery data, and performance trends. Plans evolve automatically as members progress." },
        { title: "Verified Access Control",    desc: "AI-driven identity checks via QR code, NFC card, PIN, or biometric scan. Anomaly detection flags suspicious entries across all locations in real time." },
        { title: "Progress Insights",           desc: "AI analyses body composition, strength records, and cardio benchmarks to surface trends and predict plateaus — giving trainers and members clear next steps." },
        { title: "Retention Engine",            desc: "Machine-learning models predict churn before it happens. AI triggers personalised re-engagement offers, streak rewards, and win-back campaigns automatically." },
        { title: "Operations Dashboard",        desc: "AI forecasts floor occupancy, predicts class demand, optimises trainer schedules, and highlights revenue opportunities — live across every location." },
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      heading: "Modular and transparent",
      subheading: "Pay only for the modules you need. Add or remove features as your business grows.",
      comingSoon: "COMING SOON", soon: "SOON", perMonth: "/month", perMonthEach: "/month each",
      modules: {
        business:   { name: "Business Management", features: ["Member Check-Ins (QR, Card, PIN)", "Multi-Location Dashboard", "Employee Management", "Reports & Analytics"] },
        classes:    { name: "Classes",             features: ["Scheduling (recurring & one-off)", "Self-service bookings & waitlists", "Capacity & trainer assignment", "Automated notifications"] },
        workout:    { name: "Workout & Training",  features: ["Workout Plan Creator", "Workout Logger", "Exercise Library", "Performance Reporting"] },
        diet:       { name: "Diet Planner",        features: ["Personalized meal plans", "Food database (local foods)", "Calorie & macro tracking", "Workout plan integration"] },
        store:      { name: "Store",               features: ["Product catalog & categories", "Order management & tracking", "Configurable order workflows", "Food, supplements, merch & more"] },
        whitelabel: { name: "White Label",         features: ["Personal iOS & Android apps", "Personalized website", "Custom domain", "Full brand customization"] },
        marketing:  { name: "Marketing & CRM",     features: ["CRM with member profiles", "Automated renewal reminders", "Email, SMS, Push & WhatsApp", "Onboarding automation"] },
      },
      extraLocations: { name: "Extra Locations", features: ["Full module access per location", "Separate management & reports", "Custom branding per gym", "Independent operations"] },
      calculator: {
        title: "Pricing calculator", selectAll: "Select all", extraLocations: "Extra locations",
        modules: (n) => `${n} module${n !== 1 ? "s" : ""}`,
        extraLocs: (n) => `+${n} extra location${n !== 1 ? "s" : ""}`,
        total: "Total",
      },
      cta: { sub: "Start with the essentials and add modules as you grow.", btn1: "Get started", btn2: "Talk to sales" },
      offer: {
        eyebrow: "Limited-time launch deal",
        heading: "All-in annual location pricing",
        subheading: "Lock in €499/year per gym location — every module, every member, every device. A massive discount over our monthly modular plans, available for a very limited time as we open up multi-location partnerships.",
        unit: "/year per location",
        compare: "Up to 90% off standard monthly pricing · All modules included",
        cta: "Claim offer",
      },
    },
    caseStudy: {
      eyebrow: "Case study", heading1: "Powering the", heading2: "Flex Gym Group",
      description: "3,000+ members across multiple locations — managed seamlessly on THE NOT FIT. From premium gyms to CrossFit studios to in-house restaurants, one system runs it all.",
      gyms: [
        { subtitle: "Premium gym in the capital",     members: "2,000+", highlight: "Members", features: ["Full equipment floor", "Group classes", "Personal training", "Sauna & recovery"] },
        { subtitle: "Premium seaside gym",            members: "1,500+", highlight: "Members", features: ["Seaside training", "Outdoor areas", "Seasonal programs", "Resort integration"] },
        { subtitle: "CrossFit, pilates & restaurant", members: "1,200+", highlight: "Members", features: ["CrossFit box", "Pilates studio", "In-house restaurant", "Sports nutrition bar"] },
      ],
      followInstagram: "Follow on Instagram",
      testimonial: "THE NOT FIT lets us manage 3 completely different gym concepts — from a premium fitness center to a CrossFit box with a restaurant — all under one platform. Our members love the seamless experience.",
      operator: "Multi-location fitness operator, Albania",
      statsLabels: ["Locations", "Total members", "Classes / week", "Platform"],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Questions, answered",
      subheading: "What gym owners ask us before they switch.",
      items: [
        { q: "Is there a free trial?", a: "Yes. Start with a 14-day free trial — no credit card required, and you can cancel anytime." },
        { q: "Can my gym have its own branded app?", a: "Yes. With the White Label module your members get your own iOS and Android app, website and custom domain — with your logo, colours and app icon." },
        { q: "Does it work for gyms with several locations?", a: "Yes. Run every location from one dashboard, with separate reports, branding and operations per gym. Each extra location is {location}/month." },
        { q: "How do members check in?", a: "With a QR code in the app, a card or a PIN. Every check-in appears live on your dashboard, across all locations." },
        { q: "How do payments work?", a: "Recurring memberships, payment plans and invoices are handled for you, with all major payment methods accepted." },
        { q: "What does the AI actually do?", a: "It drafts personalised diet and workout plans from each member's goals and body metrics, and surfaces trends like peak hours and members at risk of leaving. Your trainers stay in control of what gets assigned." },
        { q: "How much does it cost?", a: "You only pay for the modules you need — use the calculator above to see your monthly price. For a limited time, every module is included for {offer}/year per location." },
      ],
      contact: "Still have questions?",
      contactCta: "Email our team",
    },
    cta: {
      heading1: "Ready to scale your", heading2: "fitness business with AI?",
      description: "Join the growing number of fitness businesses using AI-powered tools from THE NOT FIT to automate and grow.\nStart your free 14-day trial today — no credit card required.",
      btn1: "Start free trial", btn2: "Talk to sales",
      check1: "14-day free trial", check2: "No credit card", check3: "Cancel anytime",
    },
    demoSubject: "Demo request — THE NOT FIT",
    footer: {
      tagline: "AI-powered fitness management platform for gyms, studios, and fitness businesses.",
      productHeading: "Product",
      productLinks: [
        { label: "Features",  href: "#features" },
        { label: "Showcase",  href: "#showcase" },
        { label: "Pricing",   href: "#pricing" },
        { label: "Customers", href: "#customers" },
        { label: "FAQ",       href: "#faq" },
      ],
      legalHeading: "Legal",
      legalLinks: [
        { label: "Privacy Policy",   href: "/privacy" },
        { label: "Terms of Service", href: "/terms" },
        { label: "Data Deletion",    href: "/data-deletion" },
        { label: "Support",          href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. All rights reserved.",
    },
  },

  // ── Albanian / Shqip ───────────────────────────────────────────────────────
  sq: {
    meta: {
      title: "THE NOT FIT — Softueri i plotë për menaxhimin e palestrës me AI",
      description: "Anëtarësime, hyrje me QR, rezervime klasash, pagesa, plane diete dhe stërvitjeje me AI dhe aplikacioni yt iOS & Android. Fillo provën falas 14-ditore.",
      ogLocale: "sq_AL",
    },
    announcement: { eyebrow: "Kohë e kufizuar", message: "50,000 ALL/vit për vendndodhje · Deri në 90% zbritje nga çmimi mujor", cta: "Shiko ofertën" },
    nav: { signIn: "Hyr", getStarted: "Fillo Tani", features: "Veçoritë", showcase: "Demo", pricing: "Çmimet", customers: "Klientët", faq: "Pyetje", menu: "Hap menunë" },
    hero: {
      badge: "Menaxhim palestre me AI",
      heading: "Platforma e plotë për",
      rotating: ["palestrat", "studiot e fitnesit", "box-et CrossFit", "franshizat"],
      rotatingSr: "palestrat, studiot e fitnesit, box-et CrossFit dhe franshizat",
      description: "Anëtarësime, hyrje me QR, rezervime klasash, pagesa, plane diete dhe stërvitjeje me AI dhe aplikacioni yt mobil me markën tënde — të gjitha në një platformë të ndërtuar për palestrat moderne.",
      cta1: "Fillo provën falas",
      cta2: "Rezervo një demo",
      footnote: ["Pa kartë krediti", "Provë 14-ditore", "Anulo në çdo kohë"],
      offerPill: "Ofertë me kohë të kufizuar · 50,000 ALL/vit për vendndodhje",
      tenantsLabel: "Një platformë, marka e çdo palestre:",
    },
    marquee: {
      heading: "Gjithçka që i duhet palestrës tënde",
      items: ["Hyrje me QR", "Rezervime klasash", "Pagesa të përsëritura", "Plane diete me AI", "Plane stërvitjeje me AI", "Dyqan në palestër", "Aplikacion iOS & Android me markën tënde", "Analitikë live", "Shumë vendndodhje", "Njoftime push"],
    },
    stats: { heading: "Fuqizon palestrat çdo ditë", gyms: "Palestra", activeMembers: "Anëtarë aktivë", uptimeSla: "Uptime SLA", support: "Mbështetje" },
    features: {
      eyebrow: "Platforma",
      heading: "Gjithçka për të drejtuar një palestër moderne",
      subheading: "Mjete modulare që automatizojnë, optimizojnë dhe personalizojnë çdo aspekt të biznesit fitness.",
      valueProp: "AI bën punën. Ti drejton palestrën.",
      cards: [
        { title: "Multi-tenant white-label",     desc_short: "Ndërtuar për franshiza dhe zinxhirë",    desc: "Menaxho vendndodhje të pakufizuara nga një platformë. Trende performancës për çdo vend, çdo palestrë me markën e saj." },
        { title: "Krijues promosh me AI",        desc_short: "Rrit të ardhurat me promo AI",           desc: "AI dizajnon promocione të targetuara, gjen kohën më të mirë dhe personalizon zbritjet për maksimum konvertimesh." },
        { title: "Planifikim klasash me AI",     desc_short: "Menaxhim rezervimesh i optimizuar",      desc: "AI analizon pranitë dhe kërkesën për të sugjeruar orare optimale, mbushur listat e pritjes dhe balancuar trajnerët." },
        { title: "Përpunimi i pagesave",         desc_short: "Menaxhim automatik i të ardhurave",      desc: "Faturim i përsëritur, plane pagesash, faturim dhe parashikime të gjeneruara nga AI. Të gjitha metodat e pagesës." },
        { title: "Analitikë parashikuese",       desc_short: "Inteligjencë biznesi në kohë reale",     desc: "AI nxjerr trende, parashikon orët kulmore, identifikon anëtarët në rrezik dhe rekomandon veprime në kohë reale." },
        { title: "Ushqim & trajnim me AI",       desc_short: "Plane personale për çdo anëtar",         desc: "AI ndërton plane diete dhe stërvitjeje nga qëllimet, metrikat trupore dhe progresi — dhe i përshtat automatikisht." },
      ],
    },
    showcase: {
      eyebrow: "Demo",
      heading: "Shiko AI në veprim",
      subheading: "Çdo palestër merr përvojën e saj me markë të plotë. Ja si duken të njëjtat mjete AI në biznese të ndryshme fitness në platformën tonë.",
      tabs: { diet: "Planifikues Diete AI", workout: "Krijues Stërvitjeje AI", promo: "Krijues Promosh AI" },
      comingSoon: "SHPEJT",
      dietDesc: "AI gjeneron plane vakte të personalizuara me gjurmim makro, baza ushqimore dhe përshtatje automatike — me markën e palestrës tënde.",
      workoutDesc: "Programe trajnimi rreth nivelit të fitnesit, rikuperimit dhe trendeve. Planet evoluojnë ndërsa anëtarët përparojnë.",
      promoDesc: "Promocione me AI që targetojnë anëtarët e duhur në kohën e duhur për të maksimizuar të ardhurat dhe mbajtjen.",
    },
    steps: {
      eyebrow: "Si funksionon",
      heading: "Gati në tre hapa",
      subheading: "Nga regjistrimi deri te hyrja e parë e anëtarit, pa kokëçarjet e zakonshme të softuerit.",
      items: [
        { title: "Krijo llogarinë", desc: "Zgjidh modulet që të duhen dhe konfiguro palestrën dhe vendndodhjet në pak minuta." },
        { title: "Bëje tëndin",     desc: "Shto logon dhe ngjyrat e markës. Anëtarët marrin një përvojë web dhe mobile me markën tënde." },
        { title: "Nis punën",       desc: "Shto anëtarët, publiko orarin e klasave dhe fillo të pranosh pagesa dhe hyrje." },
      ],
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "Aplikacione iOS & Android për çdo palestër",
      sub: "Anëtarët marrin përvojë mobile me markë të plotë. Ti mban kontrollin e çdo versioni.",
      bullet1: "Markë e personalizuar — logo, ngjyra, ikonë",
      bullet2: "Hyrje me QR, rezervime, plane dhe dyqan",
      bullet3: "Njoftime push dhe mesazhe brenda aplikacionit",
    },
    benefits: {
      eyebrow: "Pse ne",
      heading: "Pse THE NOT FIT?",
      subheading: "Platforma më e avancuar e menaxhimit të fitness me AI, e besuar nga liderët e industrisë.",
      items: [
        { title: "Menaxhim Diete me AI",              desc: "Gjeneroni plane ushqyerjeje të personalizuara në sekonda. AI adapton makrot dhe oraret e vakteve bazuar në qëllimet, alergjiitë dhe metrikat trupore." },
        { title: "Krijues Planesh Stërvitjeje me AI", desc: "Programe trajnimi rreth nivelit të fitnesit, të dhënave të rikuperimit dhe trendeve të performancës. Planet evoluojnë automatikisht." },
        { title: "Kontroll Aksesi i Verifikuar",      desc: "Verifikime identiteti me AI nëpërmjet QR kodit, kartës NFC, PIN ose skanimit biometrik. Zbulimi i anomalive sinjalizon hyrje të dyshimta në kohë reale." },
        { title: "Njohuri Progresi",                  desc: "AI analizon përbërjen trupore, rekordet e forcës dhe standardet kardio për të zbuluar trende dhe parashikuar ngecje — duke dhënë hapa të qarta." },
        { title: "Motor Mbajtjeje",                   desc: "Modelet e mësimit makinerik parashikojnë largimin para se të ndodhë. AI aktivizon automatikisht oferta riangazhimi, shpërblime dhe fushata rikthimi." },
        { title: "Panel Operacionesh",                desc: "AI parashikon zënien e katit, kërkesat e klasave, optimizon oraret e trajnerëve dhe nxjerr mundësi të ardhurash — live në çdo vendndodhje." },
      ],
    },
    pricing: {
      eyebrow: "Çmimet",
      heading: "Modulare dhe transparente",
      subheading: "Paguani vetëm për modulet që ju duhen. Shtoni ose hiqni veçori ndërsa biznesi rritet.",
      comingSoon: "SHPEJT", soon: "SHPEJT", perMonth: "/muaj", perMonthEach: "/muaj secila",
      modules: {
        business:   { name: "Menaxhimi i Biznesit",  features: ["Hyrje Anëtarësh (QR, Kartë, PIN)", "Panel Multi-Vendndodhje", "Menaxhimi i Punonjësve", "Raporte & Analitikë"] },
        classes:    { name: "Klasa",                  features: ["Planifikim (i përsëritur & njëherësh)", "Rezervime & lista pritjeje", "Kapacitet & caktim trajnerësh", "Njoftime automatike"] },
        workout:    { name: "Stërvitje & Trajnim",    features: ["Krijues i Planit të Stërvitjes", "Regjistër Stërvitjeje", "Bibliotekë Ushtrimesh", "Raportim Performancës"] },
        diet:       { name: "Planifikues Diete",      features: ["Plane vakte të personalizuara", "Bazë ushqimore (ushqime lokale)", "Gjurmim kalori & makro", "Integrim me planin e stërvitjes"] },
        store:      { name: "Dyqan",                  features: ["Katalog produktesh & kategori", "Menaxhim & gjurmim porosish", "Rrjedha e konfigurueshme", "Ushqime, suplemente, merch & më shumë"] },
        whitelabel: { name: "White Label",            features: ["Aplikacione personale iOS & Android", "Faqe interneti e personalizuar", "Domen i personalizuar", "Personalizim i plotë i markës"] },
        marketing:  { name: "Marketing & CRM",        features: ["CRM me profile anëtarësh", "Rikujtues automatikë rinovimi", "Email, SMS, Push & WhatsApp", "Automatizim onboarding"] },
      },
      extraLocations: { name: "Vendndodhje Shtesë", features: ["Akses i plotë modulesh për vendndodhje", "Menaxhim & raporte të veçanta", "Markë e personalizuar për çdo palestër", "Operacione të pavarura"] },
      calculator: {
        title: "Kalkulator Çmimesh", selectAll: "Zgjidh të gjitha", extraLocations: "Vendndodhje shtesë",
        modules: (n) => `${n} modul${n !== 1 ? "e" : ""}`,
        extraLocs: (n) => `+${n} vendndodhje shtesë`,
        total: "Totali",
      },
      cta: { sub: "Filloni me bazën dhe shtoni module ndërsa rriteni.", btn1: "Fillo Tani", btn2: "Fol me shitjet" },
      offer: {
        eyebrow: "Ofertë me kohë të kufizuar",
        heading: "Çmim vjetor i plotë për vendndodhje",
        subheading: "Sigurohuni me 50,000 ALL/vit për çdo vendndodhje palestre — të gjitha modulet, të gjithë anëtarët, të gjitha pajisjet. Një zbritje masive ndaj planeve mujore modulare, e disponueshme për një kohë shumë të kufizuar.",
        unit: "/vit për vendndodhje",
        compare: "Deri në 90% zbritje nga çmimi standard mujor · Të gjitha modulet të përfshira",
        cta: "Merr ofertën",
      },
    },
    caseStudy: {
      eyebrow: "Rast studimi", heading1: "Duke fuqizuar", heading2: "Flex Gym Group",
      description: "Mbi 3,000 anëtarë nëpër vendndodhje të shumëfishta — të menaxhuar pa probleme në THE NOT FIT. Nga palestrat premium tek studiot CrossFit dhe restorantet, një sistem bën gjithçka.",
      gyms: [
        { subtitle: "Palestër premium në kryeqytet",  members: "2,000+", highlight: "Anëtarë", features: ["Kat i plotë pajisjesh", "Klasa grupore", "Trajnim personal", "Sauna & rikuperim"] },
        { subtitle: "Palestër premium në bregdet",    members: "1,500+", highlight: "Anëtarë", features: ["Trajnim në bregdet", "Zona jashtë", "Programe sezonale", "Integrim resort"] },
        { subtitle: "CrossFit, pilates & restorant",  members: "1,200+", highlight: "Anëtarë", features: ["Box CrossFit", "Studio Pilates", "Restorant i brendshëm", "Bar ushqyerjeje sportive"] },
      ],
      followInstagram: "Ndiqni në Instagram",
      testimonial: "THE NOT FIT na lejon të menaxhojmë 3 koncepte krejt të ndryshme palestrash — nga qendra premium fitness deri te box CrossFit me restorant — të gjitha nën një platformë. Anëtarët tanë e duan përvojën e qetë.",
      operator: "Operues fitness me shumë vendndodhje, Shqipëri",
      statsLabels: ["Vendndodhje", "Anëtarë gjithsej", "Klasa / javë", "Platformë"],
    },
    faq: {
      eyebrow: "Pyetje",
      heading: "Pyetjet më të shpeshta",
      subheading: "Çfarë na pyesin pronarët e palestrave para se të kalojnë te ne.",
      items: [
        { q: "A ka provë falas?", a: "Po. Fillo me një provë falas 14-ditore — pa kartë krediti dhe mund ta anulosh në çdo kohë." },
        { q: "A mund të ketë palestra ime aplikacionin e saj?", a: "Po. Me modulin White Label anëtarët e tu marrin aplikacionin tënd iOS dhe Android, faqe interneti dhe domen të personalizuar — me logon, ngjyrat dhe ikonën tënde." },
        { q: "A funksionon për palestra me disa vendndodhje?", a: "Po. Menaxho çdo vendndodhje nga një panel i vetëm, me raporte, markë dhe operacione të veçanta për çdo palestër. Çdo vendndodhje shtesë kushton {location}/muaj." },
        { q: "Si hyjnë anëtarët në palestër?", a: "Me kod QR në aplikacion, me kartë ose me PIN. Çdo hyrje shfaqet live në panelin tënd, në të gjitha vendndodhjet." },
        { q: "Si funksionojnë pagesat?", a: "Anëtarësimet e përsëritura, planet e pagesave dhe faturat menaxhohen për ty, me të gjitha metodat kryesore të pagesës." },
        { q: "Çfarë bën saktësisht AI?", a: "Harton plane diete dhe stërvitjeje të personalizuara nga qëllimet dhe metrikat trupore të çdo anëtari dhe nxjerr në pah trende si orët kulmore dhe anëtarët në rrezik largimi. Trajnerët vendosin se çfarë u caktohet anëtarëve." },
        { q: "Sa kushton?", a: "Paguan vetëm për modulet që të duhen — përdor kalkulatorin më lart për të parë çmimin mujor. Për një kohë të kufizuar, të gjitha modulet përfshihen për {offer}/vit për vendndodhje." },
      ],
      contact: "Ke ende pyetje?",
      contactCta: "Na shkruaj",
    },
    cta: {
      heading1: "Gati të shkallëzoni", heading2: "biznesin tuaj me AI?",
      description: "Bashkohuni me bizneset fitness që përdorin mjetet me AI të THE NOT FIT për të automatizuar dhe rritur.\nFilloni provën falas 14-ditore sot — pa kartë krediti.",
      btn1: "Fillo provën falas", btn2: "Fol me shitjet",
      check1: "Provë falas 14-ditore", check2: "Pa kartë krediti", check3: "Anulo në çdo kohë",
    },
    demoSubject: "Kërkesë për demo — THE NOT FIT",
    footer: {
      tagline: "Platformë e menaxhimit të fitness me AI për palestra, studio dhe biznese fitness.",
      productHeading: "Produkti",
      productLinks: [
        { label: "Veçoritë", href: "#features" },
        { label: "Demo",     href: "#showcase" },
        { label: "Çmimet",   href: "#pricing" },
        { label: "Klientët", href: "#customers" },
        { label: "Pyetje",   href: "#faq" },
      ],
      legalHeading: "Ligjore",
      legalLinks: [
        { label: "Politika e Privatësisë", href: "/privacy" },
        { label: "Kushtet e Shërbimit",    href: "/terms" },
        { label: "Fshirja e të Dhënave",   href: "/data-deletion" },
        { label: "Mbështetje",             href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. Të gjitha të drejtat e rezervuara.",
    },
  },

  // ── Italian / Italiano ─────────────────────────────────────────────────────
  it: {
    meta: {
      title: "THE NOT FIT — Software gestionale per palestre con AI",
      description: "Abbonamenti, check-in QR, prenotazione corsi, pagamenti, piani dieta e allenamento con AI e la tua app iOS & Android brandizzata. Prova gratis per 14 giorni.",
      ogLocale: "it_IT",
    },
    announcement: { eyebrow: "Tempo limitato", message: "€499/anno per sede · Fino al 90% di sconto sul mensile", cta: "Vedi offerta" },
    nav: { signIn: "Accedi", getStarted: "Inizia", features: "Funzionalità", showcase: "Demo", pricing: "Prezzi", customers: "Clienti", faq: "FAQ", menu: "Apri il menu" },
    hero: {
      badge: "Gestione palestra con AI",
      heading: "La piattaforma completa per",
      rotating: ["palestre", "studi fitness", "box CrossFit", "catene di palestre"],
      rotatingSr: "palestre, studi fitness, box CrossFit e catene di palestre",
      description: "Abbonamenti, check-in QR, prenotazione corsi, pagamenti, piani dieta e allenamento con AI e la tua app mobile brandizzata — tutto in un'unica piattaforma pensata per le palestre moderne.",
      cta1: "Inizia la prova gratuita",
      cta2: "Prenota una demo",
      footnote: ["Nessuna carta", "Prova 14 giorni", "Cancella quando vuoi"],
      offerPill: "Offerta lancio a tempo limitato · €499/anno per sede",
      tenantsLabel: "Una piattaforma, il brand di ogni palestra:",
    },
    marquee: {
      heading: "Tutto ciò che serve alla tua palestra",
      items: ["Check-in QR", "Prenotazione corsi", "Pagamenti ricorrenti", "Piani dieta AI", "Piani allenamento AI", "Store in palestra", "App iOS & Android brandizzata", "Analisi in tempo reale", "Multi-sede", "Notifiche push"],
    },
    stats: { heading: "Al servizio delle palestre, ogni giorno", gyms: "Palestre", activeMembers: "Soci attivi", uptimeSla: "Uptime SLA", support: "Supporto" },
    features: {
      eyebrow: "Piattaforma",
      heading: "Tutto per gestire una palestra moderna",
      subheading: "Strumenti modulari che automatizzano, ottimizzano e personalizzano ogni parte del tuo business fitness.",
      valueProp: "L'AI fa il lavoro. Tu gestisci la palestra.",
      cards: [
        { title: "Multi-tenancy white-label", desc_short: "Per franchising e catene",            desc: "Gestisci sedi illimitate da un'unica piattaforma. Trend di performance per sede; ogni palestra mantiene il suo brand." },
        { title: "Promo builder con AI",      desc_short: "Aumenta i ricavi con promo smart",     desc: "L'AI progetta promozioni mirate, trova il momento migliore e personalizza gli sconti per massimizzare le conversioni." },
        { title: "Pianificazione corsi AI",   desc_short: "Gestione prenotazioni ottimizzata",    desc: "L'AI analizza presenze e domanda per suggerire orari ottimali, riempire le liste d'attesa e bilanciare gli istruttori." },
        { title: "Elaborazione pagamenti",    desc_short: "Gestione automatica dei ricavi",       desc: "Fatturazione ricorrente, piani di pagamento, fatturazione e previsioni AI. Tutti i principali metodi di pagamento." },
        { title: "Analisi predittive",        desc_short: "Business intelligence in tempo reale", desc: "L'AI evidenzia trend di ricavi, prevede ore di punta, identifica soci a rischio e raccomanda azioni in tempo reale." },
        { title: "Nutrizione & training AI",  desc_short: "Piani personali per ogni socio",       desc: "L'AI costruisce piani dieta e allenamento da obiettivi, metriche corporee e progressi — e li adatta automaticamente." },
      ],
    },
    showcase: {
      eyebrow: "Demo",
      heading: "L'AI in azione",
      subheading: "Ogni palestra ha la propria esperienza completamente personalizzata. Ecco come appaiono gli stessi strumenti AI nelle diverse attività fitness sulla piattaforma.",
      tabs: { diet: "Pianificatore Dieta AI", workout: "Costruttore Allenamenti AI", promo: "Creatore Promo AI" },
      comingSoon: "PROSSIMAMENTE",
      dietDesc: "L'AI genera piani pasto personalizzati con monitoraggio macro, database alimentari e regolazioni automatiche — con il brand di ogni palestra.",
      workoutDesc: "Programmi intelligenti basati su livello di fitness, recupero e tendenze. I piani si evolvono con i progressi dei soci.",
      promoDesc: "Promozioni AI che raggiungono i soci giusti al momento giusto per massimizzare ricavi e fidelizzazione.",
    },
    steps: {
      eyebrow: "Come funziona",
      heading: "Operativo in tre passi",
      subheading: "Dalla registrazione al primo check-in, senza i soliti grattacapi del software.",
      items: [
        { title: "Crea il tuo account", desc: "Scegli i moduli che ti servono e configura palestra e sedi in pochi minuti." },
        { title: "Rendila tua",         desc: "Aggiungi logo e colori del brand. I tuoi soci avranno un'esperienza web e mobile personalizzata." },
        { title: "Vai live",            desc: "Aggiungi i soci, pubblica il calendario dei corsi e inizia a incassare pagamenti e check-in." },
      ],
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "App iOS & Android native per ogni palestra",
      sub: "I soci ricevono un'esperienza mobile completamente personalizzata. Tu controlli ogni versione.",
      bullet1: "Brand personalizzato — logo, colori, icona",
      bullet2: "Check-in QR, prenotazioni, piani e store",
      bullet3: "Notifiche push e messaggi in-app",
    },
    benefits: {
      eyebrow: "Perché noi",
      heading: "Perché THE NOT FIT?",
      subheading: "La piattaforma di gestione fitness con AI più avanzata, scelta dai leader del settore.",
      items: [
        { title: "Gestione Dieta con AI",                   desc: "Genera piani nutrizionali personalizzati in pochi secondi. L'AI adatta i macro e gli orari dei pasti in base agli obiettivi, alle allergie e alle metriche corporee." },
        { title: "Costruttore Piani di Allenamento con AI", desc: "Programmi di allenamento basati su livello di fitness, dati di recupero e tendenze di prestazione. I piani si evolvono automaticamente." },
        { title: "Controllo Accessi Verificato",            desc: "Verifiche d'identità con AI tramite QR code, tessera NFC, PIN o scansione biometrica. Il rilevamento anomalie segnala accessi sospetti in tempo reale." },
        { title: "Analisi Progressi",                       desc: "L'AI analizza composizione corporea, record di forza e benchmark cardio per evidenziare trend e prevedere plateau — passi concreti per istruttori e soci." },
        { title: "Motore di Fidelizzazione",                desc: "Modelli ML prevedono il churn prima che avvenga. L'AI attiva automaticamente offerte personalizzate, premi streak e campagne di riconquista." },
        { title: "Dashboard Operativo",                     desc: "L'AI prevede l'occupazione, la domanda dei corsi, ottimizza gli orari e segnala opportunità di ricavo — live in ogni sede." },
      ],
    },
    pricing: {
      eyebrow: "Prezzi",
      heading: "Modulare e trasparente",
      subheading: "Paga solo per i moduli di cui hai bisogno. Aggiungi o rimuovi funzionalità man mano che il business cresce.",
      comingSoon: "PROSSIMAMENTE", soon: "PRESTO", perMonth: "/mese", perMonthEach: "/mese cad.",
      modules: {
        business:   { name: "Gestione Aziendale",         features: ["Check-in Soci (QR, Tessera, PIN)", "Dashboard Multi-Sede", "Gestione Dipendenti", "Report & Analisi"] },
        classes:    { name: "Corsi",                       features: ["Pianificazione (ricorrente & una tantum)", "Prenotazioni & liste d'attesa", "Capienza & assegnazione istruttori", "Notifiche automatiche"] },
        workout:    { name: "Allenamento & Training",      features: ["Creatore Piano di Allenamento", "Diario Allenamento", "Libreria Esercizi", "Report Prestazioni"] },
        diet:       { name: "Pianificatore Dieta",         features: ["Piani pasto personalizzati", "Database alimenti (cibi locali)", "Monitoraggio calorie & macro", "Integrazione piano allenamento"] },
        store:      { name: "Negozio",                     features: ["Catalogo prodotti & categorie", "Gestione & monitoraggio ordini", "Flussi ordini configurabili", "Cibo, integratori, merch e altro"] },
        whitelabel: { name: "White Label",                 features: ["App iOS & Android personalizzate", "Sito web personalizzato", "Dominio personalizzato", "Personalizzazione completa del brand"] },
        marketing:  { name: "Marketing & CRM",             features: ["CRM con profili soci", "Promemoria rinnovo automatici", "Email, SMS, Push & WhatsApp", "Automazione onboarding"] },
      },
      extraLocations: { name: "Sedi Aggiuntive", features: ["Accesso completo ai moduli per sede", "Gestione & report separati", "Branding personalizzato per palestra", "Operazioni indipendenti"] },
      calculator: {
        title: "Calcolatore prezzi", selectAll: "Seleziona tutti", extraLocations: "Sedi aggiuntive",
        modules: (n) => `${n} modul${n !== 1 ? "i" : "o"}`,
        extraLocs: (n) => `+${n} sed${n !== 1 ? "i" : "e"} aggiuntiv${n !== 1 ? "e" : "a"}`,
        total: "Totale",
      },
      cta: { sub: "Inizia con l'essenziale e aggiungi moduli man mano che cresci.", btn1: "Inizia ora", btn2: "Parla con le vendite" },
      offer: {
        eyebrow: "Offerta lancio a tempo limitato",
        heading: "Prezzo annuale completo per sede",
        subheading: "Blocca €499/anno per ogni sede di palestra — ogni modulo, ogni socio, ogni dispositivo. Uno sconto enorme rispetto ai piani modulari mensili, disponibile per un tempo molto limitato mentre apriamo i piani multi-sede.",
        unit: "/anno per sede",
        compare: "Fino al 90% di sconto sul prezzo standard mensile · Tutti i moduli inclusi",
        cta: "Riscatta offerta",
      },
    },
    caseStudy: {
      eyebrow: "Caso di studio", heading1: "Al servizio del", heading2: "Flex Gym Group",
      description: "Oltre 3.000 soci in più sedi — gestiti senza problemi su THE NOT FIT. Dalle palestre premium ai box CrossFit ai ristoranti interni, un solo sistema.",
      gyms: [
        { subtitle: "Palestra premium nella capitale", members: "2.000+", highlight: "Soci", features: ["Piano attrezzature completo", "Corsi di gruppo", "Personal training", "Sauna & recupero"] },
        { subtitle: "Palestra premium sul mare",        members: "1.500+", highlight: "Soci", features: ["Allenamento in riva al mare", "Aree esterne", "Programmi stagionali", "Integrazione resort"] },
        { subtitle: "CrossFit, pilates & ristorante",  members: "1.200+", highlight: "Soci", features: ["Box CrossFit", "Studio Pilates", "Ristorante interno", "Bar nutrizione sportiva"] },
      ],
      followInstagram: "Seguici su Instagram",
      testimonial: "THE NOT FIT ci permette di gestire 3 concetti di palestra completamente diversi — da un centro fitness premium a una box CrossFit con ristorante — tutto sotto un'unica piattaforma. I nostri soci adorano l'esperienza.",
      operator: "Operatore fitness multi-sede, Albania",
      statsLabels: ["Sedi", "Soci totali", "Corsi / settimana", "Piattaforma"],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Domande frequenti",
      subheading: "Cosa ci chiedono i titolari di palestre prima di passare a noi.",
      items: [
        { q: "C'è una prova gratuita?", a: "Sì. Inizia con 14 giorni di prova gratuita — nessuna carta di credito richiesta e puoi cancellare quando vuoi." },
        { q: "La mia palestra può avere un'app tutta sua?", a: "Sì. Con il modulo White Label i tuoi soci avranno la tua app iOS e Android, un sito web e un dominio personalizzato — con il tuo logo, i tuoi colori e la tua icona." },
        { q: "Funziona per palestre con più sedi?", a: "Sì. Gestisci ogni sede da un'unica dashboard, con report, branding e operatività separati per palestra. Ogni sede aggiuntiva costa {location}/mese." },
        { q: "Come fanno il check-in i soci?", a: "Con un codice QR nell'app, una tessera o un PIN. Ogni ingresso compare in tempo reale sulla dashboard, in tutte le sedi." },
        { q: "Come funzionano i pagamenti?", a: "Abbonamenti ricorrenti, piani di pagamento e fatture sono gestiti per te, con tutti i principali metodi di pagamento." },
        { q: "Cosa fa esattamente l'AI?", a: "Prepara piani dieta e allenamento personalizzati in base a obiettivi e metriche corporee di ogni socio, ed evidenzia tendenze come le ore di punta e i soci a rischio abbandono. Gli istruttori decidono cosa assegnare." },
        { q: "Quanto costa?", a: "Paghi solo i moduli che ti servono — usa il calcolatore qui sopra per vedere il prezzo mensile. Per un periodo limitato, tutti i moduli sono inclusi a {offer}/anno per sede." },
      ],
      contact: "Hai altre domande?",
      contactCta: "Scrivici",
    },
    cta: {
      heading1: "Pronto a scalare il tuo", heading2: "business fitness con l'AI?",
      description: "Unisciti alle imprese fitness che usano gli strumenti AI di THE NOT FIT per automatizzare e crescere.\nInizia la prova gratuita di 14 giorni oggi — nessuna carta di credito richiesta.",
      btn1: "Inizia la prova gratuita", btn2: "Parla con le vendite",
      check1: "Prova gratuita 14 giorni", check2: "Nessuna carta di credito", check3: "Cancella quando vuoi",
    },
    demoSubject: "Richiesta demo — THE NOT FIT",
    footer: {
      tagline: "Piattaforma di gestione fitness con AI per palestre, studi e imprese fitness.",
      productHeading: "Prodotto",
      productLinks: [
        { label: "Funzionalità", href: "#features" },
        { label: "Demo",         href: "#showcase" },
        { label: "Prezzi",       href: "#pricing" },
        { label: "Clienti",      href: "#customers" },
        { label: "FAQ",          href: "#faq" },
      ],
      legalHeading: "Legale",
      legalLinks: [
        { label: "Informativa sulla Privacy", href: "/privacy" },
        { label: "Termini di Servizio",       href: "/terms" },
        { label: "Cancellazione Dati",        href: "/data-deletion" },
        { label: "Supporto",                  href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. Tutti i diritti riservati.",
    },
  },
}
